import React from 'react';
import { Check } from 'lucide-react';
import type { MockPOSummary } from '@/types/purchaseFlow';

interface POSummaryBlockProps {
  data: unknown;
}

function fmt(n: number, currency: string): string {
  return `${currency} ${n.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
}

const POSummaryBlock: React.FC<POSummaryBlockProps> = ({ data }) => {
  const po = data as MockPOSummary;
  if (!po?.po_number) return null;

  return (
    <div className="mt-2 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-3 text-xs">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center">
          <Check className="w-3 h-3" />
        </div>
        <span className="font-semibold text-emerald-800 dark:text-emerald-300">PO Created</span>
      </div>

      <div className="space-y-1.5 text-gray-700 dark:text-gray-300">
        <div className="flex justify-between">
          <span className="text-gray-500">Number</span>
          <span className="font-mono font-medium">{po.po_number}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Vendor</span>
          <span>{po.vendor_name}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Status</span>
          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-[10px] font-medium">
            {po.status}
          </span>
        </div>

        <div className="border-t border-emerald-200 dark:border-emerald-800 pt-1.5 mt-1.5">
          <div className="text-gray-500 mb-1">Items ({po.items.length})</div>
          {po.items.map((item, i) => (
            <div key={i} className="flex justify-between ml-2 text-gray-600 dark:text-gray-400">
              <span className="truncate mr-2">{item.product_name} ×{item.quantity}</span>
              <span className="font-mono whitespace-nowrap">{fmt(item.quantity * item.unit_price, item.currency)}</span>
            </div>
          ))}
        </div>

        <div className="border-t border-emerald-200 dark:border-emerald-800 pt-1.5 space-y-0.5">
          <div className="flex justify-between text-gray-500">
            <span>Subtotal</span>
            <span className="font-mono">{fmt(po.subtotal, po.currency)}</span>
          </div>
          <div className="flex justify-between text-gray-500">
            <span>Tax (5%)</span>
            <span className="font-mono">{fmt(po.tax_amount, po.currency)}</span>
          </div>
          <div className="flex justify-between font-semibold text-gray-800 dark:text-gray-200">
            <span>Total</span>
            <span className="font-mono">{fmt(po.grand_total, po.currency)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POSummaryBlock;
